import type { ReactElement } from "react"
import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import SearchIcon from "@mui/icons-material/Search"
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import { Grid } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { Button, InputText, Pagination, Typography } from "@synapse-analytics/synapse-ui"
import type { FormikProps } from "formik"

import { safeStringify } from "../../../utils/genericHelpers"
import type { CredentialsType, ScriptDialogFormik } from "../Interfaces"

import styles from "../Scripts.module.scss"

/**
 * Renders a single row in the credentials table.
 * Handles display and editing of key and value for a credential.
 * Provides functionality to show/hide the value and remove the credential.
 *
 * @param {() => void} handleRemove - Callback function to remove the credential.
 * @param {(e: InputChangeEvent<Element>) => void} handleKeyChange - Callback function for key input change.
 * @param {(e: InputChangeEvent<Element>) => void} handleValueChange - Callback function for value input change.
 * @param {CredentialsType} feature - The credential data object.
 * @param {boolean} isReadMode - Flag indicating if the component is in read-only mode.
 * @param {boolean} isDisabled - Flag indicating if the input fields should be disabled.
 * @returns {ReactElement} A Grid container representing a table row.
 */
const CredentialItem = (props: {
  handleRemove: () => void
  handleKeyChange: (e: InputChangeEvent<Element>) => void
  handleValueChange: (e: InputChangeEvent<Element>) => void
  feature: CredentialsType
  isReadMode: boolean
  isDisabled: boolean
}): ReactElement => {
  const { handleRemove, handleKeyChange, handleValueChange, feature, isReadMode, isDisabled } = props

  const { t } = useTranslation("scripts", {
    keyPrefix: "customScript.components.credentials",
  })

  const [isShown, setIsShown] = useState<boolean>(false)

  return (
    <Grid container item xs={12} className={styles.tableRow}>
      <Grid item xs={5.5} className={styles.credentialName}>
        {isReadMode || !feature.isNew ? (
          <Typography variant="p" style={{ textOverflow: "ellipsis", width: "100%" }} noWrap>
            {feature.key}
          </Typography>
        ) : (
          <InputText
            value={safeStringify(feature.key)}
            handleChange={(e) => handleKeyChange(e)}
            placeholder={t("placeholders.key")}
            hideDescription
            fullWidth
            disabled={isDisabled}
          />
        )}
      </Grid>

      <Grid item xs={6.5} className={styles.credentialValue} justifyContent={"space-between"}>
        {/* Display masked value in read mode */}
        {isReadMode ? (
          <Typography variant="p">••••••••</Typography>
        ) : (
          <Fragment>
            <InputText
              value={feature.value ?? ""}
              handleChange={(e) => handleValueChange(e)}
              placeholder={t("placeholders.value")}
              hideDescription
              fullWidth
              disabled={isDisabled}
              type={!feature.isUpdated || isShown ? "text" : "masked"}
              endAdornment={
                feature.isUpdated ? (
                  isShown ? (
                    <VisibilityOutlinedIcon
                      style={{ color: "var(--base-text-2)" }}
                      fontSize="small"
                      onClick={() => setIsShown((prev) => !prev)}
                      sx={{ fontSize: "14px" }}
                    />
                  ) : (
                    <VisibilityOffOutlinedIcon
                      style={{ color: "var(--base-text-2)" }}
                      fontSize="small"
                      onClick={() => setIsShown((prev) => !prev)}
                      sx={{ fontSize: "14px" }}
                    />
                  )
                ) : undefined
              }
            />

            <Button onClick={async () => handleRemove()}>
              <DeleteOutlineIcon fontSize="small" style={{ fill: "var(--gray-background-1)" }} />
            </Button>
          </Fragment>
        )}
      </Grid>
    </Grid>
  )
}

/**
 * Component for managing custom script secrets (credentials).
 * Allows viewing, adding, editing, and deleting credentials either via a table interface
 * Includes pagination and search functionality for the table view.
 * Integrates with Formik for form state management.
 *
 * @param {FormikProps<any>} formik - Formik props containing form state and handlers, expected to have 'creds' in its values.
 * @param {boolean} isReadMode - Flag indicating if the component should be rendered in read-only mode.
 * @param {boolean} isLoading - Flag indicating if credential data is currently being loaded.
 * @returns {React.ReactElement} The main component UI for managing credentials.
 */
export function CustomScriptsSecrets(
  props: Readonly<{ formik: FormikProps<ScriptDialogFormik>; isReadMode: boolean; isLoading: boolean }>,
): React.ReactElement {
  const { formik, isReadMode, isLoading } = props

  const { t } = useTranslation("scripts", {
    keyPrefix: "customScript.components.credentials",
  })

  const pageSize = 6
  const [page, setPage] = useState<number>(0)
  const [searchInputValue, setSearchInputValue] = useState<string>("")

  /**
   * Handles changes to the key input field for a specific credential in the table.
   * Updates the corresponding credential's key in the formik state.
   *
   * @param {InputChangeEvent} e - The input change event object.
   * @param {number} index - The index of the credential item within the current page.
   */
  const handleKeyChange = (e: InputChangeEvent, index: number): void => {
    const newTableData = [...formik.values.creds]

    const oldCred = newTableData[+page * +pageSize + index]

    if (!oldCred) return

    newTableData[+page * +pageSize + index] = {
      ...oldCred,
      key: `${e.target.value}`,
    }

    formik.setFieldValue("creds", newTableData)
  }

  /**
   * Handles changes to the value input field for a specific credential in the table.
   * Updates the value and marks the credential as updated in formik state.
   * Removes masking characters ('*') if editing a previously saved masked value.
   *
   * @param {InputChangeEvent} e - The input change event object.
   * @param {number} index - The index of the credential item within the current page.
   */
  const handleValueChange = (e: InputChangeEvent, index: number): void => {
    const newTableData = [...formik.values.creds]

    const oldCred = newTableData[+page * +pageSize + index]

    if (!oldCred) return

    // If the credential hasn't been updated yet (since load) and value starts with masking characters,
    // assume user is editing a masked value, so remove all asterisks from the input.
    if (!oldCred.isUpdated && oldCred.value.startsWith("********")) {
      // Ensure newValue is treated as string before replacing
      e.target.value = (e.target.value as string).replaceAll("*", "")
    }

    newTableData[+page * +pageSize + index] = {
      ...oldCred,
      value: `${e.target.value}`,
      isUpdated: true,
    }

    formik.setFieldValue("creds", newTableData)
  }

  /**
   * Adds a new, empty credential entry to the beginning of the table data.
   * Resets the pagination to the first page.
   * Updates the 'creds' field in formik state.
   */
  const handleAddCredential = (): void => {
    // Create new credential object and add it to the beginning of the array
    const newTableData = [{ key: "", value: "", isNew: true, isUpdated: true }, ...formik.values.creds]
    formik.setFieldValue("creds", newTableData)
    setSearchInputValue("")
    setPage(0)
  }

  /**
   * Removes a credential from the table data based on its index within the current page.
   * Updates the 'creds' and 'deletedCred' fields in formik state.
   *
   * @param {number} index - The index of the credential item within the current page.
   */
  const handleRemove = (index: number): void => {
    const newTableData = formik.values.creds
    newTableData.splice(+page * +pageSize + index, 1)
    // Set a flag indicating a credential was deleted
    formik.setFieldValue("deletedCred", true)
    formik.setFieldValue("creds", newTableData)

    // go back one page automatically if the last page has no more items to show
    // doesn't go back if there are no more pages
    newTableData.length <= page * pageSize && page - 1 >= 0 && setPage(page - 1)
  }

  const filteredTableData = formik.values.creds.filter((cred: CredentialsType) =>
    cred.key.toLowerCase().includes(searchInputValue.toLowerCase()),
  )

  return (
    <Grid container item p={2} className={styles.outerContainer} direction={"column"} flexWrap={"nowrap"}>
      <Grid container item gap={2} mb={isReadMode ? 2 : 1}>
        <Typography variant={"a"}>{t("title")}</Typography>

        {!isReadMode && (
          <Grid container item xs={12} justifyContent={"space-between"} alignItems={"flex-end"}>
            <InputText
              value={searchInputValue}
              id="search-bar"
              startAdornment={<SearchIcon fontSize="small" style={{ marginTop: "2px", color: "var(--base-text-2)" }} />}
              placeholder={t("placeholders.search")}
              handleChange={(e: InputChangeEvent) => {
                setSearchInputValue(e.target.value.toString())
                setPage(0)
              }}
              variant="filled"
              hideDescription
            />

            <Button variant="secondary" size="small" onClick={handleAddCredential}>
              {t("buttons.addCredential")}
            </Button>
          </Grid>
        )}
      </Grid>

      {filteredTableData.length > 0 ? (
        <Grid item xs={12}>
          <Grid item xs={12} className={styles.tableContainer} mb={2}>
            {filteredTableData
              .slice(page * pageSize, page * pageSize + pageSize)
              ?.map((cred: CredentialsType, index: number) => {
                return (
                  <CredentialItem
                    key={index}
                    handleRemove={() => handleRemove(index)}
                    isReadMode={isReadMode}
                    handleKeyChange={(e) => {
                      handleKeyChange(e, index)
                    }}
                    handleValueChange={(e) => {
                      handleValueChange(e, index)
                    }}
                    feature={cred}
                    isDisabled={formik.isSubmitting}
                  />
                )
              })}
          </Grid>
        </Grid>
      ) : isLoading ? (
        <Grid item xs={12} className={styles.tableContainer} alignContent={"center"} justifyItems={"center"}>
          <Typography variant="h3-regular" variantColor={2}>
            {t("loading")}
          </Typography>
        </Grid>
      ) : (
        <Grid item xs={12} className={styles.tableContainer} alignContent={"center"} justifyItems={"center"}>
          <Typography variant="h3-regular" variantColor={2}>
            {t("emptyState")}
          </Typography>
        </Grid>
      )}

      {filteredTableData.length > 0 && (
        <Pagination
          hidePageSize
          variant="simple"
          size="small"
          totalCount={filteredTableData.length}
          page={page + 1}
          onPageChange={(index: number) => {
            setPage(index - 1)
          }}
          pageSize={pageSize}
        />
      )}
    </Grid>
  )
}
